/** Threat Rating and reward multipliers: the numbers shown on every mission card (02 §12, 04 §7). */

import { clamp } from '../sim/math';
import type { ArchetypeId, ModifierId } from '../sim/types';
import { archetype, ARCHETYPES } from './content/archetypes';
import { MAP, THREAT_RAMP } from './content/map';
import { MODIFIER_RULES, MODIFIERS } from './content/modifiers';
import type { Operation, TheatreNode } from './types';
import { round2 } from './util';

/** Readiness bonus: 0 at 0-2, +1 at 3-5, +2 at 6-8, +3 at 9-10. */
export function readinessBonus(readiness: number): number {
  if (readiness >= 9) return 3;
  if (readiness >= 6) return 2;
  return readiness >= 3 ? 1 : 0;
}

const HEAVIEST = Math.max(...ARCHETYPES.map((a) => a.baseThreat));

/**
 * How far through the operation a node lies: 0 for the first column of the first theatre, 1 for
 * the last theatre's keystone (which sits one column past the last column of choice).
 */
export function nodeProgress(theatre: number, column: number, theatres: number): number {
  const perTheatre = MAP.columns + 1;
  const last = Math.max(1, theatres * perTheatre - 1);
  return clamp((theatre * perTheatre + column) / last, 0, 1);
}

/** The highest rating a node this far along may have before readiness is added. */
export function threatCap(difficulty: number, progress: number): number {
  const slider = clamp(difficulty, 0, 3);
  return Math.round(THREAT_RAMP.startCap + slider + (HEAVIEST - THREAT_RAMP.startCap) * clamp(progress, 0, 1));
}

/**
 * TR = archetype base + difficulty slider (0-3), held under the progress cap, + readiness bonus,
 * clamped 1-9. `progress` defaults to 1: the full rating, as Classic and Practice fly it. The
 * readiness bonus sits outside the cap, so a loud campaign still makes the early nodes harder.
 */
export function threatFor(id: ArchetypeId, difficulty: number, readiness: number, progress = 1): number {
  const full = archetype(id).baseThreat + clamp(difficulty, 0, 3);
  return clamp(Math.min(full, threatCap(difficulty, progress)) + readinessBonus(readiness), 1, 9);
}

/** The rating a node of a running operation shows on its card and flies at; 0 for a support node. */
export function nodeThreat(op: Pick<Operation, 'difficulty' | 'readiness' | 'theatres'>, node: TheatreNode): number {
  if (!node.params) return 0;
  const progress = nodeProgress(node.theatre, node.column, op.theatres.length);
  return threatFor(node.params.archetype, op.difficulty, op.readiness, progress);
}

/** Product of the modifiers' rewards, and x1.1 for a cross-terrain variant. */
export function rewardMultiplier(modifiers: readonly ModifierId[], variant: boolean): number {
  let m = variant ? MODIFIER_RULES.variantReward : 1;
  for (const id of modifiers) m *= MODIFIERS.find((x) => x.id === id)?.reward ?? 1;
  return round2(m);
}

/**
 * May `candidate` join `present` on a mission of this archetype? With `threat`, a card that only comes to
 * missions rated higher (`ModifierInfo.minThreat`, ROUND8's Dog Patrols) is refused below it.
 */
export function modifierAllowed(
  candidate: ModifierId, present: readonly ModifierId[], id: ArchetypeId, threat?: number,
): boolean {
  if (present.includes(candidate)) return false;
  const info = MODIFIERS.find((x) => x.id === candidate);
  if (!info || info.excludes.includes(id)) return false;
  if (threat !== undefined && threat < (info.minThreat ?? 0)) return false;
  const conflictsOf = (id: ModifierId): readonly ModifierId[] => MODIFIERS.find((x) => x.id === id)?.conflicts ?? [];
  return !present.some((p) => info.conflicts.includes(p) || conflictsOf(p).includes(candidate));
}
